import type { TFunction } from "i18next";
import type { ILrsConfig, ILrsConnectionTest } from "./state/lrsSlice";

const PREFIX = "pages.profile.organization.lrs";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const lrsErrorCode = (payload: unknown): string | null => {
  if (!isRecord(payload) || !isRecord(payload.error)) {
    return null;
  }
  const details = payload.error.details;
  return isRecord(details) && typeof details.code === "string" ? details.code : null;
};

export const lrsRequestErrorMessage = (t: TFunction, payload: unknown): string => {
  const code = lrsErrorCode(payload);
  const generic = t(`${PREFIX}.errors.generic`);
  return code ? t(`${PREFIX}.errors.${code}`, { defaultValue: generic }) : generic;
};

export const lrsTestMessage = (t: TFunction, result: ILrsConnectionTest): string => {
  if (result.ok) {
    const versions = (result.versions ?? []).join(", ") || "?";
    return result.readDenied
      ? t(`${PREFIX}.test.okWriteOnly`, { versions })
      : t(`${PREFIX}.test.ok`, { versions });
  }
  const generic = t(`${PREFIX}.test.failed`, { status: result.httpStatus ?? "?" });
  return result.errorCode
    ? t(`${PREFIX}.test.${result.errorCode}`, {
        status: result.httpStatus ?? "?",
        defaultValue: generic,
      })
    : generic;
};

export const lrsSendErrorMessage = (t: TFunction, config: ILrsConfig): string | null => {
  if (!config.last_error && !config.last_error_code) {
    return null;
  }
  const code = config.last_error_code;
  const generic = t(`${PREFIX}.sendErrors.generic`);
  if (!code) {
    return generic;
  }
  const http = /^http_(\d+)$/.exec(code);
  if (http) {
    const status = Number(http[1]);
    const key =
      status === 401 || status === 403
        ? "httpAuth"
        : status === 429
        ? "httpRateLimited"
        : status >= 500
        ? "httpServer"
        : "http";
    return t(`${PREFIX}.sendErrors.${key}`, { status });
  }
  return t(`${PREFIX}.sendErrors.${code}`, { defaultValue: generic });
};
